import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { PackageProviderName } from 'data/ppoints/constants';
import { useMemo } from 'react';
import { MappingCountryMarkets } from './useMappingCountryMarkets';

/** One provider's rate mapping for one country, ready to render as a row inside that country's card. */
export interface MarketRateCountryProviderEntry {
    provider: PackageProviderName;
    market: MappingCountryMarkets;
    values: number[];
    rateOptions: SelectMultiChoicelistOption<number>[];
    hasOptionsError: boolean;
    onChange(rateIds: number[]): void;
    onRatesUpdated?(): void;
}

export interface MarketRateCountryGroup {
    countryCode: string;
    /** Same regardless of which provider reported it — market existence isn't provider-specific. */
    hasMarket: boolean;
    /** Every active provider that has a rate mapping for this country. */
    providers: MarketRateCountryProviderEntry[];
}

/**
 * One active provider's already-fetched country list — the pieces of `useProviderRateMappingData`'s
 * return this hook needs to regroup. Fetching stays where it already happens (one
 * `useProviderRateMappingData` call per provider, in its own component — the provider list's length
 * can change between renders, and hooks can't be called a variable number of times) — this hook only
 * takes the results after the fact.
 */
export interface ProviderMarketRateSource {
    provider: PackageProviderName;
    withMarket: MappingCountryMarkets[];
    withoutMarket: MappingCountryMarkets[];
    getRates(countryCode: string): number[];
    setRates(countryCode: string, rateIds: number[]): void;
    getOptions(countryCode: string): SelectMultiChoicelistOption<number>[];
    optionsError?: Error;
    onRatesUpdated?(): void;
}

/**
 * Regroups every active provider's own per-country rate mapping — normally one card per provider,
 * listing that provider's countries — by COUNTRY instead, the way `useZonesWithMappedProviders`
 * groups by shipping zone (one card per zone, listing every provider assigned to it). The result is
 * a `Map` keyed by country code, each entry listing every provider active in that country, so
 * `MarketRateStep` can render one country card per entry with one row per provider inside it —
 * mirroring `RateForProviderSelector`'s one-zone-card-lists-every-provider layout.
 */
export const useMarketRateCountryGroups = (sources: ProviderMarketRateSource[]): Map<string, MarketRateCountryGroup> =>
    useMemo(() => {
        const groups = new Map<string, MarketRateCountryGroup>();

        sources.forEach((source) => {
            [...source.withMarket, ...source.withoutMarket].forEach((market) => {
                const group = groups.get(market.countryCode) ?? {
                    countryCode: market.countryCode,
                    hasMarket: market.markets.length > 0,
                    providers: [],
                };

                group.providers.push({
                    provider: source.provider,
                    market,
                    values: source.getRates(market.countryCode),
                    rateOptions: source.getOptions(market.countryCode),
                    hasOptionsError: Boolean(source.optionsError),
                    onChange: (rateIds) => source.setRates(market.countryCode, rateIds),
                    onRatesUpdated: source.onRatesUpdated,
                });

                groups.set(market.countryCode, group);
            });
        });

        return groups;
    }, [sources]);
